import { Input } from '@repo/ui/components/input';
import { useState } from 'react';

import { fieldSurface } from '../auth/AuthFrame';

/**
 * The most a typed quantity can be: three digits.
 *
 * The largest party this counter has sold is 175 adults (#263), and a school
 * group of several hundred still fits. A fourth digit is refused rather than
 * read, so `175` with a stray `0` stays 175 instead of charging for 1,750 — and
 * on admission each one is its own recorded ticket and its own row to void.
 */
export const TYPED_QUANTITY_MAX = 999;

/** Digits only, three at most — an empty box is somebody about to type. */
const TYPED = /^\d{0,3}$/;

/**
 * How many of a basket line, typed — beside the steppers, not instead of them
 * (#263). Nobody taps + 175 times; the steppers stay for the one-more case.
 *
 * **Text with a numeric keypad, not `type="number"`**, for the reason
 * `RecordExpense` gives: that hands the browser a spinner, a scroll wheel that
 * changes the figure under a pointer, and an empty value that reads back as
 * `NaN`.
 *
 * **A control on the basket, not a form.** Nothing is submitted from it; it sets
 * a line the screen already holds. A keystroke that is not a digit, or would be
 * a fourth, does not land. Nought is shown refused and never reaches the line,
 * and leaving the box puts back the quantity the line actually holds — so what
 * is sold is always a number the cashier can see.
 *
 * **`max` is clamped to, not refused** (#295): `20` against a line held to the
 * 4 the records show lands 4, and the box shows 4 at once rather than a figure
 * the line is not holding.
 */
export function QuantityField({
  name,
  quantity,
  max = TYPED_QUANTITY_MAX,
  disabled,
  onQuantity,
}: {
  name: string;
  quantity: number;
  max?: number;
  disabled: boolean;
  onQuantity: (quantity: number) => void;
}) {
  /** What is being typed, or `null` when the box shows the line. */
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <Input
      // `md:` as well, or the registry's `md:text-sm` wins at every till width and
      // the figure reads smaller than the amount beside it.
      className={`${fieldSurface} h-touch w-20 shrink-0 text-center font-mono text-lg numeric md:text-lg`}
      inputMode="numeric"
      autoComplete="off"
      aria-label={`Quantity of ${name}`}
      // Empty is unfinished, not wrong; nought is wrong.
      aria-invalid={draft !== null && draft !== '' && Number(draft) === 0}
      placeholder={String(quantity)}
      value={draft ?? String(quantity)}
      disabled={disabled}
      onFocus={(event) => {
        // Typed over, not appended to: `1` then 175 is 175.
        event.currentTarget.select();
      }}
      onChange={(event) => {
        const typed = event.currentTarget.value;

        if (!TYPED.test(typed)) return;

        if (Number(typed) > max) {
          setDraft(String(max));
          onQuantity(max);

          return;
        }

        setDraft(typed);

        if (Number(typed) > 0) onQuantity(Number(typed));
      }}
      onBlur={() => {
        setDraft(null);
      }}
    />
  );
}
